import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { usePlayBilling } from "@/hooks/usePlayBilling";
import { PRODUCT_IDS, BILLING_STATUS, formatPlayPrice } from "@/lib/playBilling";
import { toast } from "sonner";
import { Check, Crown, Clock, Loader2, RotateCcw, ShieldCheck, AlertTriangle } from "lucide-react";

function PlanCard({ testId, label, period, price, highlight, disabled, busy, onClick, buyLabel }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-4 flex flex-col gap-3 ${highlight ? "border-cyan-400/40 bg-cyan-400/10" : "border-white/10 bg-white/[0.03]"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{period}</p>
        </div>
        {highlight && (
          <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-cyan-400/15 text-cyan-200 border border-cyan-400/30">
            {highlight}
          </span>
        )}
      </div>
      <p data-testid={`${testId}-price`} className="text-2xl font-display font-bold text-cyan-300 tabular-nums">{price}</p>
      <Button
        data-testid={`${testId}-buy`}
        onClick={onClick}
        disabled={disabled}
        className="w-full h-10 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold disabled:opacity-60"
      >
        {busy ? <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> : buyLabel}
      </Button>
    </div>
  );
}

export function UpgradeModal({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const billing = usePlayBilling({ enabled: open });
  const { supported, catalog, purchasing, restoring, lastError, buy, restore, loadCatalog, clearError } = billing;

  const features = [t("upgrade.f1"), t("upgrade.f2"), t("upgrade.f3"), t("upgrade.f4"), t("upgrade.f5")];
  // Display locale for Google Play supplied prices (presentation only): EN uses en-US,
  // PL and VI use the existing pl-PL display.
  const locale = lang === "en" ? "en-US" : "pl-PL";

  useEffect(() => {
    if (!open) clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleBuy = async (productId) => {
    const item = catalog.items?.[productId];
    const res = await buy(productId, { currency: item?.price?.currency || "PLN" });
    if (res.ok) {
      toast.success(t("billing.success"));
      onOpenChange(false);
    } else if (res.status === "cancelled") {
      toast(t("billing.cancelled"));
    } else if (res.errorKey) {
      toast.error(t(`billing.err.${res.errorKey}`));
    }
  };

  const handleRestore = async () => {
    const res = await restore();
    if (res.restored) {
      toast.success(t("billing.restored"));
      onOpenChange(false);
    } else if (res.errorKey) {
      toast.error(t(`billing.err.${res.errorKey}`));
    } else {
      toast(t("billing.err.nothingActive"));
    }
  };

  const monthly = catalog.items?.[PRODUCT_IDS.monthly];
  const yearly = catalog.items?.[PRODUCT_IDS.yearly];
  const busy = !!purchasing || restoring;

  // Purchase UI is only rendered when the Digital Goods API is present AND
  // Google Play returned a catalog. Everything else falls back to the
  // informational (non-purchase) message.
  const showPurchaseUi = supported && (catalog.status === BILLING_STATUS.LOADING || catalog.status === BILLING_STATUS.READY);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md bg-surface-1 border-white/10 max-h-[90vh] overflow-y-auto" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Crown className="text-cyan-300" style={{ width: 22, height: 22 }} /> {t("upgrade.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("settings.goPremiumDesc")}</DialogDescription>
        </DialogHeader>
        <div className="mt-1">
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-display font-bold text-cyan-300">{t("upgrade.premium")}</span>
            {!showPurchaseUi && <span className="text-sm text-muted-foreground">{t("upgrade.oneTime")}</span>}
          </div>
          <ul className="space-y-2.5 mb-6">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400/15 border border-cyan-400/30">
                  <Check className="text-cyan-300" style={{ width: 12, height: 12 }} />
                </span>
                {f}
              </li>
            ))}
          </ul>

          {user?.is_premium ? (
            <div data-testid="upgrade-already-premium" className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 p-4 text-center">
              <ShieldCheck className="text-cyan-300 mx-auto mb-2" style={{ width: 22, height: 22 }} />
              <p className="text-sm font-medium text-cyan-100">{t("billing.alreadyPremium")}</p>
            </div>
          ) : showPurchaseUi ? (
            <div data-testid="upgrade-play-billing" className="space-y-3">
              {catalog.status === BILLING_STATUS.LOADING ? (
                <div data-testid="upgrade-play-loading" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Skeleton className="h-36 rounded-xl bg-white/5" />
                  <Skeleton className="h-36 rounded-xl bg-white/5" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <PlanCard
                    testId="upgrade-plan-monthly"
                    label={t("billing.monthly")}
                    period={t("billing.perMonth")}
                    price={formatPlayPrice(monthly?.price, locale)}
                    disabled={busy || !monthly}
                    busy={purchasing === PRODUCT_IDS.monthly}
                    onClick={() => handleBuy(PRODUCT_IDS.monthly)}
                    buyLabel={t("billing.buy")}
                  />
                  <PlanCard
                    testId="upgrade-plan-yearly"
                    label={t("billing.yearly")}
                    period={t("billing.perYear")}
                    price={formatPlayPrice(yearly?.price, locale)}
                    highlight={t("landing.bestValue")}
                    disabled={busy || !yearly}
                    busy={purchasing === PRODUCT_IDS.yearly}
                    onClick={() => handleBuy(PRODUCT_IDS.yearly)}
                    buyLabel={t("billing.buy")}
                  />
                </div>
              )}

              {lastError && (
                <div data-testid="upgrade-billing-error" className="flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-100">
                  <AlertTriangle style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0" />
                  <span>{t(`billing.err.${lastError}`)}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-1">
                <p className="text-[11px] text-muted-foreground leading-snug">{t("billing.playNotice")}</p>
                <Button
                  data-testid="upgrade-restore-button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={handleRestore}
                  className="shrink-0 text-cyan-200 hover:bg-cyan-400/10 h-8"
                >
                  {restoring ? <Loader2 className="animate-spin mr-1" style={{ width: 14, height: 14 }} /> : <RotateCcw className="mr-1" style={{ width: 14, height: 14 }} />}
                  {t("billing.restore")}
                </Button>
              </div>
            </div>
          ) : supported && catalog.status === BILLING_STATUS.ERROR ? (
            <div data-testid="upgrade-play-error" className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-center">
              <AlertTriangle className="text-amber-300 mx-auto mb-2" style={{ width: 22, height: 22 }} />
              <p className="text-sm font-medium text-amber-100">{t("billing.catalogError")}</p>
              <Button data-testid="upgrade-play-retry" variant="outline" size="sm" onClick={loadCatalog} className="mt-3 border-white/15 bg-white/5 hover:bg-white/10">
                {t("billing.retry")}
              </Button>
            </div>
          ) : (
            /* Web / unsupported: informational fallback only. No external payment flow. */
            <div data-testid="upgrade-coming-soon-message" className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 p-4 text-center">
              <Clock className="text-cyan-300 mx-auto mb-2" style={{ width: 22, height: 22 }} />
              <p className="text-sm font-medium text-cyan-100">{t("upgrade.appComingSoon")}</p>
              <p className="text-xs text-muted-foreground mt-1.5">{t("upgrade.appComingSoonHint")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
